import React, { useMemo, useState } from 'react'
import {
  SortFilter,
  SORT_FILTER_DEFAULT_FILTER_OPTIONS,
  SORT_FILTER_DEFAULT_SORT_SECTIONS,
  SORT_FILTER_PREVIEW_STATE_LABELS,
  SORT_FILTER_PREVIEW_STATES,
  type SortFilterMode,
  type SortFilterPreviewState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './SortFilterShowcase.css'

const SortFilterShowcase: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false)
  const [mode, setMode] = useState<SortFilterMode>('sort')
  const [previewState, setPreviewState] =
    useState<SortFilterPreviewState>('open')
  const [sortValues, setSortValues] = useState<Record<string, string>>({
    sortBy: 'name',
    order: 'ascending',
  })
  const [selectedFilters, setSelectedFilters] = useState<string[]>([
    'name',
    'fileType',
    'dateModified',
  ])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'sort-filter-mode',
            label: 'Mode',
            control: (
              <select
                id="sort-filter-mode"
                className="ui-field-control"
                value={mode}
                onChange={(event) =>
                  setMode(event.target.value as SortFilterMode)
                }
              >
                <option value="sort">Sort</option>
                <option value="filter">Filter</option>
              </select>
            ),
          },
          {
            id: 'sort-filter-state',
            label: 'Highlighted state',
            control: (
              <select
                id="sort-filter-state"
                className="ui-field-control"
                value={previewState}
                onChange={(event) =>
                  setPreviewState(event.target.value as SortFilterPreviewState)
                }
              >
                {SORT_FILTER_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {SORT_FILTER_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'sort-filter-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="sort-filter-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, mode, previewState],
  )

  const handleSortChange = (sectionId: string, optionId: string): void => {
    setSortValues((prev) => ({ ...prev, [sectionId]: optionId }))
  }

  return (
    <>
      <UiPreviewCanvas
        title="Sort + filter"
        description="Icon trigger with a sort (single-select) or filter (multi-select) dropdown — as used in Files and similar browse headers."
        stageClassName="ui-preview__stage--wide sort-filter-showcase__stage"
      >
        <div
          className={[
            'sort-filter-showcase__sheet',
            darkMode ? 'sort-filter-showcase__sheet--dark' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Click the sort or filter icon to open the menu.
            </p>
            <div className="sort-filter-showcase__live-row">
              <SortFilter
                mode="sort"
                darkMode={darkMode}
                sections={SORT_FILTER_DEFAULT_SORT_SECTIONS}
                values={sortValues}
                onChange={handleSortChange}
              />
              <SortFilter
                mode="filter"
                darkMode={darkMode}
                options={SORT_FILTER_DEFAULT_FILTER_OPTIONS}
                selectedIds={selectedFilters}
                onChange={setSelectedFilters}
              />
            </div>
            <p className="sort-filter-showcase__status">
              Sort: {sortValues.sortBy} / {sortValues.order}
              {' · '}
              Filter: {selectedFilters.join(', ') || 'none'}
            </p>
          </section>

          <section className="sort-filter-showcase__sample">
            <h3 className="ui-showcase-live__title">Selected state</h3>
            <p className="ui-showcase-live__note">
              Shows the Highlighted state from the properties panel.
            </p>
            <div
              className={[
                'sort-filter-showcase__sample-cell',
                previewState === 'open'
                  ? 'sort-filter-showcase__sample-cell--open'
                  : '',
                darkMode ? 'sort-filter-showcase__sample-cell--dark' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <h4 className="sort-filter-showcase__label">
                {SORT_FILTER_PREVIEW_STATE_LABELS[previewState]} ·{' '}
                {mode === 'sort' ? 'Sort' : 'Filter'}
              </h4>
              {mode === 'sort' ? (
                <SortFilter
                  mode="sort"
                  darkMode={darkMode}
                  previewState={previewState}
                  sections={SORT_FILTER_DEFAULT_SORT_SECTIONS}
                  values={sortValues}
                />
              ) : (
                <SortFilter
                  mode="filter"
                  darkMode={darkMode}
                  previewState={previewState}
                  options={SORT_FILTER_DEFAULT_FILTER_OPTIONS}
                  selectedIds={selectedFilters}
                />
              )}
            </div>
          </section>
        </div>
      </UiPreviewCanvas>
      <UiPropertiesPanel
        componentName="SortFilter"
        sections={propertySections}
      />
    </>
  )
}

export default SortFilterShowcase
